"use client";

import React from 'react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceLine,
} from 'recharts';
import { Activity } from 'lucide-react';
import { useLanguage } from '@/lib/language-context';
import { t } from '@/lib/translations';
import { useThreatTimeline } from '@/lib/supabase/realtime-hooks';

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-[#0F1117] border border-cyber-border rounded-lg p-3 shadow-xl">
        <p className="text-[11px] text-cyber-muted font-mono mb-2">{label}</p>
        {payload.map((entry: any) => (
          <div key={entry.dataKey} className="flex items-center gap-2 mb-1">
            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: entry.color }} />
            <span className="text-[11px] text-cyber-muted capitalize">{entry.dataKey}:</span>
            <span className="text-[11px] font-mono font-medium text-cyber-text">{entry.value}</span>
          </div>
        ))}
      </div>
    );
  }
  return null;
};

function ThreatTimelineSkeleton() {
  return (
    <div className="h-[220px] flex flex-col gap-3 pt-2" aria-busy="true" aria-label="Loading threat timeline">
      {/* Y-axis + chart area skeleton */}
      <div className="flex gap-2 h-full">
        {/* Y-axis labels */}
        <div className="flex flex-col justify-between pb-5 w-6 shrink-0">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="w-5 h-2 rounded skeleton-cyber" />
          ))}
        </div>
        {/* Chart area with fake grid lines and wave shapes */}
        <div className="flex-1 relative overflow-hidden rounded-lg">
          {/* Horizontal grid lines */}
          {Array.from({ length: 5 }).map((_, i) => (
            <div
              key={i}
              className="absolute w-full h-px bg-white/5"
              style={{ top: `${(i / 4) * 100}%` }}
            />
          ))}
          {/* Skeleton wave bars */}
          <div className="absolute inset-0 flex items-end gap-1 px-1 pb-5">
            {Array.from({ length: 24 }).map((_, i) => {
              const heights = [30, 45, 25, 60, 40, 70, 35, 55, 80, 45, 30, 65, 50, 40, 75, 35, 60, 45, 55, 70, 40, 50, 35, 45];
              return (
                <div
                  key={i}
                  className="flex-1 rounded-t skeleton-cyber opacity-60"
                  style={{ height: `${heights[i % heights.length]}%` }}
                />
              );
            })}
          </div>
          {/* X-axis labels */}
          <div className="absolute bottom-0 left-0 right-0 flex justify-between px-1">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="w-6 h-2 rounded skeleton-cyber" />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ThreatTimeline({ timeRange }: { timeRange: '24h' | '7d' | '30d' }) {
  const { language } = useLanguage();
  const { data: rawData, loading, error } = useThreatTimeline();

  const chartData = rawData.map((row) => ({
    time: row.time_label,
    critical: row.critical_count,
    high: row.high_count,
    medium: row.medium_count,
    blocked: row.blocked_count,
  }));

  // Find spike points (top 2 critical values)
  const spikeLabels = [...chartData]
    .sort((a, b) => b.critical - a.critical)
    .slice(0, 2)
    .map((d) => d.time);

  return (
    <div className="cyber-card p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Activity size={15} className="text-cyber-blue" />
          <h3 className={`text-sm font-semibold text-white ${language === 'bn' ? 'font-bangla' : ''}`}>
            {t(language, 'threat_timeline')}
          </h3>
          <div className="flex items-center gap-1 ml-2">
            <div className={`w-1.5 h-1.5 rounded-full ${loading ? 'skeleton-cyber' : 'bg-cyber-red live-dot'}`} />
            <span className="text-[10px] text-cyber-muted font-mono">{loading ? 'LOADING' : 'LIVE'}</span>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {loading ? (
            <div className="hidden lg:flex items-center gap-3">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="w-14 h-3 rounded skeleton-cyber" />
              ))}
            </div>
          ) : (
            <div className="hidden lg:flex items-center gap-3 text-[10px] font-mono text-cyber-muted">
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-cyber-red inline-block" />Critical</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-orange-400 inline-block" />High</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-yellow-400 inline-block" />Medium</span>
            </div>
          )}
        </div>
      </div>

      {error ? (
        <div className="h-[220px] flex items-center justify-center">
          <p className="text-xs text-cyber-red font-mono">Failed to load timeline data</p>
        </div>
      ) : loading ? (
        <ThreatTimelineSkeleton />
      ) : (
        <ResponsiveContainer width="100%" height={220}>
          <AreaChart data={chartData} margin={{ top: 5, right: 5, bottom: 5, left: -10 }}>
            <defs>
              <linearGradient id="criticalGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#FF3B3B" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#FF3B3B" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="highGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#FF6B35" stopOpacity={0.25} />
                <stop offset="95%" stopColor="#FF6B35" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="mediumGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#FFB800" stopOpacity={0.2} />
                <stop offset="95%" stopColor="#FFB800" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#1A2035" vertical={false} />
            <XAxis
              dataKey="time"
              tick={{ fill: '#6B7A99', fontSize: 10, fontFamily: 'IBM Plex Mono' }}
              axisLine={false}
              tickLine={false}
              interval={3}
            />
            <YAxis
              tick={{ fill: '#6B7A99', fontSize: 10, fontFamily: 'IBM Plex Mono' }}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip content={<CustomTooltip />} />
            {spikeLabels.map((label) => (
              <ReferenceLine
                key={label}
                x={label}
                stroke="#FF3B3B"
                strokeDasharray="3 3"
                strokeOpacity={0.5}
                label={{ value: 'Spike', position: 'top', fill: '#FF3B3B', fontSize: 9, fontFamily: 'IBM Plex Mono' }}
              />
            ))}
            <Area type="monotone" dataKey="medium" stroke="#FFB800" strokeWidth={1.5} fill="url(#mediumGrad)" />
            <Area type="monotone" dataKey="high" stroke="#FF6B35" strokeWidth={1.5} fill="url(#highGrad)" />
            <Area type="monotone" dataKey="critical" stroke="#FF3B3B" strokeWidth={2} fill="url(#criticalGrad)" />
          </AreaChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}